// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import Radio, { RadioPresentationProps } from "@store/MobiusOverrides/Radio/Radio";
import RadioGroup, { RadioGroupPresentationProps, RadioGroupProps } from "@store/MobiusOverrides/RadioGroup/RadioGroup";
import validateRequiredTextField from "@store/Framework/Common/Utilities/validateRequiredTextField";
import { BaseFormFieldProps, FormFieldValue } from "@store/Framework/Types/FormField";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { spacingStyles, visuallyHiddenStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import getColor from "@insite/mobius/utilities/getColor";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FieldDefinition, { ListFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";

export const productTypeFormFieldLabelKey = "Product Type Choice";

type FieldValueType = string;
export type ProductTypeFormFieldValue = FormFieldValue<FieldValueType>;

export interface ProductTypeOption {
    label: string;
    value?: string;
}

export const enum productTypeFormFieldFields {
    productTypeOptions = "productTypeOptions",
    productTypeOptionLabel = "productTypeOptionLabel",
    productTypeOptionValue = "productTypeOptionValue",
}

export const productTypeFormFieldFieldDefinitions: FieldDefinition[] = [
    {
        name: productTypeFormFieldFields.productTypeOptions,
        getDisplay: x => x.fields[productTypeFormFieldFields.productTypeOptionLabel] || "",
        editorTemplate: "ListField",
        defaultValue: [],
        fieldType: "Contextual",
        fieldDefinitions: [
            {
                name: productTypeFormFieldFields.productTypeOptionLabel,
                displayName: "Label",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: productTypeFormFieldFields.productTypeOptionValue,
                displayName: "Value",
                editorTemplate: "TextField",
                defaultValue: "",
            },
        ],
    },
];

export const useParseProductTypeFormFieldFields = <T extends HasFields["fields"]>(fields: T) => {
    const rawOptions: ListFieldDefinition["defaultValue"] | undefined =
        fields[productTypeFormFieldFields.productTypeOptions];

    return useMemo(() => {
        const options = rawOptions?.map(({ fields }): ProductTypeOption => {
            return {
                label: fields[productTypeFormFieldFields.productTypeOptionLabel],
                value: fields[productTypeFormFieldFields.productTypeOptionValue],
            };
        });

        return { options };
    }, [rawOptions]);
};

export interface ProductTypeFormFieldPresentationProps extends ExpandStyles<ProductTypeFormFieldStyles> {}

export interface ProductTypeFormFieldProps
    extends Omit<RadioGroupProps, keyof RadioGroupPresentationProps | "defaultValue" | "error" | "value">,
        ProductTypeFormFieldPresentationProps,
        BaseFormFieldProps<FieldValueType> {
    options: ProductTypeOption[] | undefined;
}

export interface ProductTypeFormFieldStyles {
    radioGroup?: RadioGroupPresentationProps;
    radioListWrapper?: InjectableCss;
    radio?: RadioPresentationProps;
}

const productTypeFormFieldStyles: ProductTypeFormFieldStyles = {
    radioGroup: {
        labelProps: {
            css: css`
                ${visuallyHiddenStyles()}
            `,
        },
        css: css`
            ${spacingStyles(null, pxToRem(24))}
        `,
    },
    radioListWrapper: {
        css: css`
            display: grid;
            grid-template-columns: repeat(1, 1fr);
            gap: ${pxToRem(16)};

            ${breakpoint(390)} {
                grid-template-columns: repeat(2, 1fr);
            }

            ${breakpoint("sm")} {
                grid-template-columns: repeat(4, 1fr);
            }
        `,
    },
    radio: {
        mergeCss: true,
        css: css`
            margin: 0 !important;

            input {
                + label {
                    width: 100%;
                    height: 100%;
                    align-items: flex-start;
                    border: ${pxToRem(12)} solid transparent;
                    border-radius: ${pxToRem(4)};
                    background-color: ${getColor("common.accent")};
                    word-break: normal;
                }

                &:checked + label {
                    color: ${getColor("primary")};
                }
            }
        `,
    },
};

const ProductTypeFormField: FC<ProductTypeFormFieldProps> = ({
    defaultValue = "",
    extendedStyles,
    label,
    options,
    onChangeHandler,
    onUpdate,
    onValidate,
    overridedStyles,
    required,
    showError = true,
    ...radioGroupProps
}) => {
    const styles = useExpandProps(productTypeFormFieldStyles, extendedStyles, overridedStyles);
    const [value, setValue] = useState(defaultValue);
    const [error, setError] = useState("");
    const textLabel = typeof label === "string" ? label : translate(`${productTypeFormFieldLabelKey}`);

    const validate = (value: string) => {
        let errorMessage: string | undefined;

        if (required) {
            errorMessage = validateRequiredTextField(value, textLabel);
        }

        errorMessage = onValidate?.({ isValid: !errorMessage, value }) ?? errorMessage;

        setError(errorMessage || "");

        return !errorMessage;
    };

    useEffect(() => {
        const isValid = validate(value);
        onUpdate?.({ isValid, value });
    }, []);

    if (!options?.length) {
        return null;
    }

    return (
        <RadioGroup
            {...styles.radioGroup}
            name="productType"
            {...radioGroupProps}
            error={showError ? error : ""}
            label={label || textLabel}
            onChangeHandler={event => {
                onChangeHandler?.(event);
                const value = event.target.value;
                setValue(value);
                const isValid = validate(value);
                onUpdate?.({ isValid, value });
            }}
            required={required}
            value={value}
        >
            <StyledWrapper {...styles.radioListWrapper}>
                {options.map(({ label, value }) => {
                    const _value = value || label;

                    return (
                        <Radio {...styles.radio} key={_value} value={_value}>
                            {label}
                        </Radio>
                    );
                })}
            </StyledWrapper>
        </RadioGroup>
    );
};

export default ProductTypeFormField;
